import { useEffect, useState } from 'react';
import tw from 'twin.macro';
import { useTranslation } from 'react-i18next';

import { Dropdown, IDropdownOption, Option } from '@components/Dropdown';
import {
  SegmentCreatorData,
  useSegmentCreatorContext,
} from '@insights_module/context/SegmentCreatorContext';
import { AQLQueryCondition, AQLQueryType } from '@insights_module/adc_ql/types';
import { FilterInput } from '@insights_module/components/FilterInput';
import { useFilterTypeOptions } from '@insights_module/hooks/useFilterTypeOptions';

export const SegmentCreatorNamedEntitiesItem = ({
  segmentData,
}: {
  segmentData: SegmentCreatorData;
}) => {
  const { condition, value, operator } = segmentData;
  const { t } = useTranslation();
  const { pushSegmentData, isPresentational } = useSegmentCreatorContext();

  const { dropdownOptions } = useFilterTypeOptions();

  const conditionOptions: IDropdownOption[] = [
    {
      value: 'includes',
      meta: { operator: 'eq' },
      label: <Option>{t('insights.query_builder.conditions.is')}</Option>,
    },
    {
      value: 'excludes',
      meta: { operator: 'eq' },
      label: <Option>{t('insights.query_builder.conditions.is_not')}</Option>,
    },
    {
      value: 'includes',
      meta: { operator: 'wildcard' },
      label: <Option>{t('insights.query_builder.conditions.contains')}</Option>,
    },
    {
      value: 'excludes',
      meta: { operator: 'wildcard' },
      label: <Option>{t('insights.query_builder.conditions.does_not_contain')}</Option>,
    },
  ];

  const [selectedType, setSelectedType] = useState<IDropdownOption>(
    dropdownOptions.find((item) => item.value === segmentData.type) || dropdownOptions[0]
  );
  const [selectedCondition, setSelectedCondition] = useState<IDropdownOption>(
    conditionOptions.find(
      (item) => item.value === (condition as unknown as string) && item.meta?.operator === operator
    ) || conditionOptions[0]
  );
  const [selectedNamedEntity, setSelectedNamedEntity] = useState<string>((value as string) || '');

  useEffect(() => {
    if (selectedType) {
      const type = selectedType.value as string;
      pushSegmentData({
        ...segmentData,
        type: type as AQLQueryType,
        category: null,
        value: null,
        condition: null,
        negation: false,
      });
    }
  }, [selectedType]);

  useEffect(() => {
    if (selectedCondition && selectedNamedEntity) {
      const segment = {
        ...segmentData,
        condition: selectedCondition.value as unknown as Exclude<
          AQLQueryCondition,
          'includes' | 'excludes'
        >,
        operator: (selectedCondition.meta?.operator || 'eq') as 'wildcard' | 'eq',
        value: selectedNamedEntity,
      };
      pushSegmentData(segment);
    }
  }, [selectedCondition, selectedNamedEntity]);

  return (
    <div tw='flex gap-1 items-center'>
      <Dropdown
        options={dropdownOptions}
        value={selectedType}
        onChange={(val) => setSelectedType(val as IDropdownOption)}
        labelStyle={tw`bg-spec-entites-filter-bg border border-spec-entites-filter-stroke text-spec-entites-filter-text font-semibold`}
        caretStyle={tw`fill-spec-entites-filter-text`}
        dropdownMenuStyle={tw`w-[178px]`}
        isPresentational={isPresentational}
      />

      <Dropdown
        //for now we don't want to show contains conditions on creating named entities because of performance issues
        options={conditionOptions.slice(0, 2)}
        value={selectedCondition}
        onChange={(val) => setSelectedCondition(val as IDropdownOption)}
        isPresentational={isPresentational}
      />

      <FilterInput
        focus
        value={selectedNamedEntity}
        contentEditable={!isPresentational}
        placeholder='Enter named entity'
        onPressEnter={(value) => setSelectedNamedEntity(value as string)}
      />
    </div>
  );
};
